import { TeamMember, LoginRequest, LoginResponse, ApiResponse, SiteSettings, Subscriber, Memory, BuildVideo } from '@/types';

// Build-time API base. Comes from VITE_API_URL, set per environment in the
// committed .env.production / .env.staging files (and .env.local for dev).
// Trailing slash stripped so `${base}/api/...` never doubles up.
const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:8787').replace(/\/+$/, '');

const TOKEN_KEY = 'mx_admin_token';

class ApiClient {
    private baseUrl: string;
    private token: string | null;

    constructor(baseUrl: string) {
        this.baseUrl = baseUrl;
        this.token = typeof localStorage !== 'undefined' ? localStorage.getItem(TOKEN_KEY) : null;
    }

    private setToken(token: string | null) {
        this.token = token;
        if (typeof localStorage === 'undefined') return;
        if (token) localStorage.setItem(TOKEN_KEY, token);
        else localStorage.removeItem(TOKEN_KEY);
    }

    private async request<T>(
        endpoint: string,
        options?: RequestInit
    ): Promise<ApiResponse<T>> {
        try {
            const response = await fetch(`${this.baseUrl}${endpoint}`, {
                ...options,
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    // Bearer token works cross-origin where cookies are blocked
                    ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
                    ...options?.headers,
                },
            });

            if (!response.ok) {
                const error = await response.json().catch(() => ({ error: 'Request failed' }));
                return { error: error.error || `HTTP ${response.status}` };
            }

            const data = await response.json();
            return { data };
        } catch (error) {
            return { error: error instanceof Error ? error.message : 'Network error' };
        }
    }

    /** Worker endpoints reply with `{ data: X }`, and request() wraps the parsed
     *  body again as `{ data: { data: X } }`. Peel the inner layer so memory
     *  callers receive X directly (mirrors the unwrap CrewBand does inline). */
    private unwrap<T>(res: ApiResponse<unknown>): ApiResponse<T> {
        if (res.error) return { error: res.error };
        const body = res.data as { data?: unknown } | unknown;
        const inner = body && typeof body === 'object' && 'data' in (body as object)
            ? (body as { data: unknown }).data
            : body;
        return { data: inner as T };
    }

    // Public API
    async getTeamMembers(): Promise<ApiResponse<TeamMember[]>> {
        return this.request<TeamMember[]>('/api/team');
    }

    async getTeamMember(id: string): Promise<ApiResponse<TeamMember>> {
        return this.request<TeamMember>(`/api/team/${id}`);
    }

    // Admin Authentication
    async login(credentials: LoginRequest): Promise<ApiResponse<LoginResponse>> {
        const res = await this.request<LoginResponse>('/api/admin/login', {
            method: 'POST',
            body: JSON.stringify(credentials),
        });
        if (res.data?.token) this.setToken(res.data.token);
        return res;
    }

    async logout(): Promise<ApiResponse<void>> {
        const res = await this.request<void>('/api/admin/logout', {
            method: 'POST',
        });
        this.setToken(null);
        return res;
    }

    async checkAuth(): Promise<ApiResponse<{ authenticated: boolean }>> {
        return this.request<{ authenticated: boolean }>('/api/admin/check');
    }

    // Admin - Team Members
    async createTeamMember(member: Omit<TeamMember, 'id' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<TeamMember>> {
        return this.request<TeamMember>('/api/admin/team', {
            method: 'POST',
            body: JSON.stringify(member),
        });
    }

    async updateTeamMember(id: string, member: Partial<TeamMember>): Promise<ApiResponse<TeamMember>> {
        return this.request<TeamMember>(`/api/admin/team/${id}`, {
            method: 'PUT',
            body: JSON.stringify(member),
        });
    }

    async deleteTeamMember(id: string): Promise<ApiResponse<void>> {
        return this.request<void>(`/api/admin/team/${id}`, {
            method: 'DELETE',
        });
    }

    async reorderTeamMembers(ids: string[]): Promise<ApiResponse<void>> {
        return this.request<void>('/api/admin/team-reorder', {
            method: 'PUT',
            body: JSON.stringify({ ids }),
        });
    }

    // Newsletter / settings
    async subscribe(email: string, source: string): Promise<ApiResponse<{ ok: boolean }>> {
        return this.request<{ ok: boolean }>('/api/subscribe', {
            method: 'POST',
            body: JSON.stringify({ email, source }),
        });
    }

    async getSettings(): Promise<ApiResponse<SiteSettings>> {
        return this.request<SiteSettings>('/api/settings');
    }

    async adminGetSettings(): Promise<ApiResponse<SiteSettings>> {
        return this.request<SiteSettings>('/api/admin/settings');
    }

    async adminUpdateSettings(settings: SiteSettings): Promise<ApiResponse<SiteSettings>> {
        return this.request<SiteSettings>('/api/admin/settings', {
            method: 'PUT',
            body: JSON.stringify(settings),
        });
    }

    async getSubscribers(): Promise<ApiResponse<Subscriber[]>> {
        return this.request<Subscriber[]>('/api/admin/subscribers');
    }

    async deleteSubscriber(id: string): Promise<ApiResponse<void>> {
        return this.request<void>(`/api/admin/subscribers/${id}`, { method: 'DELETE' });
    }

    subscribersCsvUrl(): string {
        return `${this.baseUrl}/api/admin/subscribers?format=csv`;
    }

    async sendBroadcast(payload: { subject: string; html: string; test: boolean; testEmail?: string }):
        Promise<ApiResponse<{ sent: number }>> {
        return this.request<{ sent: number }>('/api/admin/broadcast', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }

    // ── Memory Wall ── (all unwrap the worker's { data: X } envelope)
    async leaveMemory(payload: { text: string; initials?: string; turnstileToken?: string; source: string }): Promise<ApiResponse<Memory>> {
        return this.unwrap<Memory>(await this.request('/api/memories', { method: 'POST', body: JSON.stringify(payload) }));
    }
    async getMemories(cursor?: string): Promise<ApiResponse<{ memories: Memory[]; nextCursor: string | null }>> {
        const q = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
        return this.unwrap(await this.request(`/api/memories${q}`));
    }
    async keepMemory(id: string, visitorToken: string): Promise<ApiResponse<{ keepCount: number; alreadyKept: boolean }>> {
        return this.unwrap(await this.request(`/api/memories/${id}/keep`, { method: 'POST', body: JSON.stringify({ visitorToken }) }));
    }
    async reportMemory(id: string): Promise<ApiResponse<{ ok: boolean }>> {
        return this.unwrap(await this.request(`/api/memories/${id}/report`, { method: 'POST', body: JSON.stringify({}) }));
    }
    async getForgotten(): Promise<ApiResponse<{ memories: Memory[] }>> {
        return this.unwrap(await this.request('/api/memories/forgotten'));
    }
    async resurrectMemory(id: string): Promise<ApiResponse<{ ok: boolean }>> {
        return this.unwrap(await this.request(`/api/memories/${id}/resurrect`, { method: 'POST', body: JSON.stringify({}) }));
    }

    // ── Build feed (YouTube RSS mirror) ──
    async getBuildVideos(): Promise<ApiResponse<{ videos: BuildVideo[] }>> {
        return this.unwrap(await this.request('/api/build/videos'));
    }

    // ── Admin Memory Moderation ──
    async adminGetMemories(status?: string): Promise<ApiResponse<any[]>> {
        const q = status ? `?status=${status}` : '';
        return this.unwrap(await this.request(`/api/admin/memories${q}`));
    }
    async adminSetMemoryStatus(id: string, status: string): Promise<ApiResponse<{ ok: boolean }>> {
        return this.unwrap(await this.request(`/api/admin/memories/${id}`, { method: 'PUT', body: JSON.stringify({ status }) }));
    }
    async adminDeleteMemory(id: string): Promise<ApiResponse<{ ok: boolean }>> {
        return this.unwrap(await this.request(`/api/admin/memories/${id}`, { method: 'DELETE' }));
    }

    // ── Analytics ──
    async adminGetEventSummary(days = 30): Promise<ApiResponse<{ days: number; total: number; byName: { name: string; count: number }[]; byDay: { day: string; count: number }[]; visits: { totalVisits: number; totalPageViews: number; byDay: { day: string; visits: number }[] } | null }>> {
        return this.unwrap(await this.request(`/api/admin/events/summary?days=${days}`));
    }

    // Image Upload
    async uploadImage(file: File): Promise<ApiResponse<{ url: string }>> {
        const formData = new FormData();
        formData.append('image', file);

        try {
            const response = await fetch(`${this.baseUrl}/api/admin/upload`, {
                method: 'POST',
                credentials: 'include',
                body: formData,
            });

            if (!response.ok) {
                const error = await response.json().catch(() => ({ error: 'Upload failed' }));
                return { error: error.error || `HTTP ${response.status}` };
            }

            const data = await response.json();
            return { data };
        } catch (error) {
            return { error: error instanceof Error ? error.message : 'Upload error' };
        }
    }
}

export const api = new ApiClient(API_BASE);
